/**
 * Minimap — bottom-right SVG overview of the farm with player + remote dots.
 *
 * Player dot is centered, the world rotates around it. Remote players appear
 * as small colored dots, scaled by hat color. Stages and zones drawn as
 * background outlines.
 */

import * as THREE from 'three';
import { ZONES } from '@shared/zones';
import type { ZoneClient } from '@/net/ZoneClient';

const HAT_HEX = [
  '#ff5f6d',
  '#ffc371',
  '#7ce0a3',
  '#56c2ff',
  '#bd72ff',
  '#ff8ec0',
  '#f5f0e8',
  '#3a2a44'
];

export class Minimap {
  private container: HTMLDivElement;
  private svg: SVGSVGElement;
  private ns = 'http://www.w3.org/2000/svg';
  private staticLayer: SVGGElement;
  private dynamicLayer: SVGGElement;

  constructor(private net: ZoneClient) {
    const root = document.getElementById('ui-root')!;
    this.container = document.createElement('div');
    this.container.className = 'minimap';
    root.appendChild(this.container);

    this.svg = document.createElementNS(this.ns, 'svg') as SVGSVGElement;
    this.svg.setAttribute('viewBox', '-500 -500 1000 1000');
    this.container.appendChild(this.svg);

    this.staticLayer = document.createElementNS(this.ns, 'g') as SVGGElement;
    this.svg.appendChild(this.staticLayer);
    this.dynamicLayer = document.createElementNS(this.ns, 'g') as SVGGElement;
    this.svg.appendChild(this.dynamicLayer);

    this.drawStatic();
  }

  private drawStatic(): void {
    for (const zone of Object.values(ZONES)) {
      const c = document.createElementNS(this.ns, 'circle');
      c.setAttribute('cx', String(zone.center[0]));
      c.setAttribute('cy', String(zone.center[1]));
      c.setAttribute('r', String(zone.radius * 0.7));
      c.setAttribute('fill', 'rgba(255,195,113,0.05)');
      c.setAttribute('stroke', 'rgba(255,195,113,0.4)');
      c.setAttribute('stroke-width', '4');
      this.staticLayer.appendChild(c);
      const t = document.createElementNS(this.ns, 'text');
      t.setAttribute('x', String(zone.center[0]));
      t.setAttribute('y', String(zone.center[1] + 6));
      t.setAttribute('text-anchor', 'middle');
      t.setAttribute('fill', 'rgba(245,240,232,0.65)');
      t.setAttribute('font-size', '24');
      t.textContent = shortLabel(zone.id);
      this.staticLayer.appendChild(t);
    }
  }

  update(player: THREE.Vector3, hatColor: number): void {
    while (this.dynamicLayer.firstChild) this.dynamicLayer.removeChild(this.dynamicLayer.firstChild);

    // Player marker
    const me = document.createElementNS(this.ns, 'circle');
    me.setAttribute('cx', String(player.x));
    me.setAttribute('cy', String(player.z));
    me.setAttribute('r', '14');
    me.setAttribute('fill', HAT_HEX[hatColor % HAT_HEX.length]);
    me.setAttribute('stroke', '#ffffff');
    me.setAttribute('stroke-width', '4');
    this.dynamicLayer.appendChild(me);

    // Remote players
    for (const rp of this.net.remotePlayers.values()) {
      const dot = document.createElementNS(this.ns, 'circle');
      dot.setAttribute('cx', String(rp.x));
      dot.setAttribute('cy', String(rp.z));
      dot.setAttribute('r', '8');
      dot.setAttribute('fill', HAT_HEX[rp.hatColor % HAT_HEX.length]);
      dot.setAttribute('stroke', 'rgba(0,0,0,0.5)');
      dot.setAttribute('stroke-width', '2');
      this.dynamicLayer.appendChild(dot);
    }
  }
}

function shortLabel(zoneId: string): string {
  const map: Record<string, string> = {
    'arch': 'Arch',
    'centeroo-core': 'Centeroo',
    'what-stage': 'What',
    'which-stage': 'Which',
    'this-that-tents': 'Tents',
    'other-stage': 'Other',
    'woods': 'Woods',
    'outeroo-pods': 'Outeroo',
    'ferris': 'Ferris'
  };
  return map[zoneId] ?? zoneId;
}
